import { useState, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  IconButton,
  Collapse,
  Checkbox,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useAuth } from "../../../../../auth";
import { useQuery } from "@tanstack/react-query";
import {
  getFindAccessProfileQueryOptions,
  getListPermissionQueryOptions,
} from "../../../../../api/bpm/endpoints";
import type {
  SmartPermissionDtoApi,
  AccessProfileWithAllPropertiesDto,
} from "../../../../../api/bpm/model";
import {
  CategoryNode,
  PermissionLeaf,
  buildPermissionTree,
} from "../../../../../utils/permissionTreeMapper";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/__auth/admin/profiles/$profileId/")({
  component: ProfileViewPage,
  staticData: {
    title: "routes.viewProfile",
  },
});

function PermissionTreeSubcategory({ perm }: { perm: PermissionLeaf }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 0.25,
          cursor:
            perm.actions && perm.actions.length > 0 ? "pointer" : "default",
          "&:hover": { bgcolor: "action.hover", borderRadius: 1 },
        }}
        onClick={() =>
          perm.actions && perm.actions.length > 0 && setExpanded(!expanded)
        }
      >
        <IconButton
          size="small"
          sx={{
            mr: 0.5,
            visibility:
              perm.actions && perm.actions.length > 0 ? "visible" : "hidden",
          }}
        >
          {expanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <Checkbox
          checked={perm.checked}
          indeterminate={perm.someChecked}
          disabled
          size="small"
          sx={{ p: 0.5 }}
        />
        <Typography
          variant="body2"
          sx={{
            ml: 1,
            fontWeight:
              typeof perm.checked === "boolean" &&
              perm.checked &&
              !perm.someChecked
                ? 600
                : 400,
          }}
        >
          {perm.label}
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 6 }}>
          {perm.actions &&
            perm.actions.map((action) => (
              <Box
                key={action.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  py: 0.25,
                }}
              >
                <Checkbox
                  checked={action.checked}
                  disabled
                  size="small"
                  sx={{ p: 0.5 }}
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {action.label}
                </Typography>
              </Box>
            ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function PermissionTreeCategory({ category }: { category: CategoryNode }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 0.5,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover", borderRadius: 1 },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <IconButton size="small" sx={{ mr: 0.5 }}>
          {expanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <Checkbox
          checked={category.allChecked}
          indeterminate={category.someChecked}
          disabled
          size="small"
          sx={{ p: 0.5 }}
        />
        <Typography
          variant="body2"
          sx={{
            ml: 1,
            fontWeight:
              typeof category.allChecked === "boolean" && category.allChecked
                ? 600
                : 400,
          }}
        >
          {category.name}
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 4 }}>
          {category.permissions.map((perm) => (
            <PermissionTreeSubcategory key={perm.id} perm={perm} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function ProfileViewPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { profileId } = Route.useParams();
  const tenant = user?.tenant || "default-domain";

  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    ...getFindAccessProfileQueryOptions(tenant, parseInt(profileId)),
  });

  const { data: allPermissionsData, isLoading: isLoadingAllPermissions } = useQuery({
    ...getListPermissionQueryOptions({
            size: 9999,
            page: 0,
            locale: "pt"
        } as any),
  });

  const selectedPermissionIds = useMemo(() => {
    return (
      (profileData as AccessProfileWithAllPropertiesDto)?.permissions?.map(
        (p) => p.id!,
      ) || []
    );
  }, [profileData]);

  const permissionTree = useMemo(() => {
    let allPerms: SmartPermissionDtoApi[] = [];
    if (Array.isArray(allPermissionsData)) {
      allPerms = allPermissionsData;
    } else if (allPermissionsData && typeof allPermissionsData === "object") {
      const page = allPermissionsData as any;
      if (Array.isArray(page.content)) {
        allPerms = page.content;
      }
    }
    return buildPermissionTree(allPerms, selectedPermissionIds);
  }, [allPermissionsData, selectedPermissionIds]);

  if (isLoadingProfile || isLoadingAllPermissions) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Box
          sx={{
            width: 4,
            height: 40,
            bgcolor: "primary.main",
            borderRadius: 1,
            mr: 2,
          }}
        />
        <Typography variant="h5" fontWeight={600}>
          {t("admin.profiles.viewTitle")}
        </Typography>
      </Box>

      <Stack spacing={2.5} sx={{ mb: 4 }}>
        <TextField
          label="Nome do Perfil"
          value={(profileData as any)?.name || ""}
          fullWidth
          size="small"
          slotProps={{ input: { readOnly: true } }}
        />
        <TextField
          label={t("admin.common.description")}
          value={(profileData as any)?.description || ""}
          fullWidth
          size="small"
          multiline
          rows={3}
          slotProps={{ input: { readOnly: true } }}
        />
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Permissões de Acesso
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        {permissionTree.length > 0 ? (
          <Box sx={{ pl: 2 }}>
            {permissionTree.map((category) => (
              <PermissionTreeCategory key={category.name} category={category} />
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
            Nenhuma permissão atribuída.
          </Typography>
        )}
      </Paper>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={() =>
            navigate({ to: "/admin/profiles", search: { page: 1, size: 10 } })
          }
        >
          VOLTAR
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => navigate({ to: `/admin/profiles/${profileId}/edit` })}
        >{t("common.edit").toUpperCase()}</Button>
      </Box>
    </Container>
  );
}
